import type { Job } from "@eai-toolkit/client";
import {
  Paper,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";

import { formatTimestamp } from "@helpers/utils";

import { isRestartingState } from "../helpers";
import RelaunchControl from "./RelaunchControl";
import StatusIndicator from "./StatusIndicator";

interface JobTableProps {
  jobs?: Job[];
  handleClickRestart: (id: string) => void;
}

const HeaderRow = styled(TableRow)(() => ({
  "& > th:nth-last-of-type(2)": { borderRight: "none" },
}));

const JobTable = (props: JobTableProps) => {
  const { jobs, handleClickRestart } = props;

  return (
    <TableContainer component={Paper}>
      <Table aria-label="simple table">
        <TableHead>
          <HeaderRow>
            <TableCell>Account</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Resources</TableCell>
            <TableCell>Datasets</TableCell>
            <TableCell>Last Run</TableCell>
            <TableCell style={{ maxWidth: 180 }}>State</TableCell>
            <TableCell style={{ width: 120 }} />
          </HeaderRow>
        </TableHead>
        {jobs ? (
          <TableBody>
            {jobs.map((job: Job) => {
              const lastRun = job.runs
                ?.map((r) =>
                  Math.round(new Date(r.startedOn ?? Date.now()).getTime()),
                )
                .sort()[0];
              const lastRunTimestamp = lastRun
                ? new Date(lastRun).toISOString()
                : null;
              const sourceDataNames = job.data
                ?.filter((d) => /:ro$/.test(d))
                .map((d) => d.split(":")[0])
                .filter((d) => !d.startsWith("shared.notebooks_support."));
              return (
                <TableRow key={job.id}>
                  <TableCell>{job.account}</TableCell>
                  <TableCell component="th" scope="row">
                    {job.name}
                  </TableCell>
                  <TableCell>
                    {job.resources?.cpu ?? 1} CPU(s), {job.resources?.mem ?? 1}
                    GB RAM, {job.resources?.gpu ?? 0} GPU(s),{" "}
                    {job.resources?.gpuMem
                      ? `${job.resources.gpuMem}GB GPU memory`
                      : "Unspecified GPU memory"}
                  </TableCell>
                  <TableCell>{sourceDataNames?.join("\n")}</TableCell>
                  <TableCell>
                    {lastRunTimestamp ? formatTimestamp(lastRunTimestamp) : "-"}
                  </TableCell>
                  <TableCell>
                    <span
                      style={{ textAlign: "center" }}
                      className={
                        isRestartingState(job.state!) ? "pulse-opacity" : ""
                      }
                    >
                      <StatusIndicator value={job.state!} />
                    </span>
                  </TableCell>
                  <TableCell>
                    <RelaunchControl
                      job={job}
                      state={job.state!}
                      handleClickRestart={() => handleClickRestart(job.id)}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        ) : (
          <TableBody>
            <div>No data</div>
          </TableBody>
        )}
      </Table>
    </TableContainer>
  );
};

export default JobTable;
